import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  CssBaseline,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Box,
  Button,
  Tabs,
  Tab,
  IconButton,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  Home as HomeIcon,
  CalendarMonth,
  Info,
  Login,
  Logout,
  BookmarkBorder,
  AdminPanelSettings,
  Phone,
  Email,
  LocationOn,
  AccessTime,
  Coffee,
  Print,
  Groups,
  LocalBar,
  People,
} from "@mui/icons-material";
import { User, onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../Firebase/firebase.ts";
import { auditLog } from "../Firebase/auditLog.ts";
import { isUserAdmin } from "../Firebase/firebaseApi.ts";
import { initializePostHog, identifyUser, resetUser, trackEvent } from "../Analytics/posthog.ts";
import "./App.css";
import Booking from "../Booking/Booking.tsx";
import SignIn from "../SignIn/SignIn.tsx";
import MyBookings from "../MyBookings/MyBookings.tsx";
import Admin from "../Admin/Admin.tsx";
import BackgroundWrapper from "../components/BackgroundWrapper.tsx";
import ErrorBoundary from "../components/ErrorBoundary.tsx";

// Create a theme with RTL support and Hebrew fonts
const theme = createTheme({
  direction: "rtl",
  typography: {
    fontFamily: "Comix No2 CLM, Heebo, Arial, sans-serif",
  },
  palette: {
    primary: {
      main: "#ac5246",
    },
    secondary: {
      main: "#388e3c",
    },
  },
});

// Navigation component
interface NavigationProps {
  user: User | null;
}

function Navigation({ user }: NavigationProps) {
  const location = useLocation();

  const getTabValue = () => {
    switch (location.pathname) {
      case "/":
        return 0;
      case "/booking":
        return 1;
      case "/my-bookings":
        return 2;
      case "/about":
        return 3;
      case "/admin":
        return false; // Admin is now in navbar, no tab selected
      case "/signin":
        return false; // No tab selected for signin page
      default:
        return 0;
    }
  };

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
      <Tabs value={getTabValue()} centered sx={{ direction: "rtl" }}>
        <Tab
          label="דף הבית"
          icon={<HomeIcon />}
          component={Link}
          to="/"
          onClick={() => trackEvent('nav_home_clicked', { user_email: user?.email })}
          sx={{ minHeight: 64 }}
        />
        {user && (
          <Tab
            label="הזמנת חדרים"
            icon={<CalendarMonth />}
            component={Link}
            to="/booking"
            onClick={() => trackEvent('nav_booking_clicked', { user_email: user.email })}
            sx={{ minHeight: 64 }}
          />
        )}
        {user && (
          <Tab
            label="ההזמנות שלי"
            icon={<BookmarkBorder />}
            component={Link}
            to="/my-bookings"
            onClick={() => trackEvent('nav_my_bookings_clicked', { user_email: user.email })}
            sx={{ minHeight: 64 }}
          />
        )}
        <Tab
          label="קצת עלינו"
          icon={<Info />}
          component={Link}
          to="/about"
          onClick={() => trackEvent('nav_about_clicked', { user_email: user?.email })}
          sx={{ minHeight: 64 }}
        />
      </Tabs>
    </Box>
  );
}

interface HomeProps {
  user: User | null;
}

function Home({ user }: HomeProps) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: "url(/home.jpeg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        position: "relative",
        display: "flex",
        alignItems: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)", // Dark overlay for better text readability
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ py: 8, direction: "rtl", position: "relative", zIndex: 2 }}>
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
            <Box
              component="img"
              src="/hub-icon.png"
              alt="חומת שמואל האב"
              sx={{
                height: { xs: "120px", sm: "200px", md: "300px" }, // Responsive height: mobile (120px), tablet (200px), desktop (300px)
                width: "auto",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
              }}
            />
          </Box>
          <Typography variant="h2" component="h1" sx={{ mb: 4, color: "white", fontWeight: "bold", textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
            ברוכים הבאים
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, color: "white", fontWeight: "bold", textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
            מערכת הזמנת חדרים ותחנות
          </Typography>

        {user && (
          <Typography variant="h6" sx={{ mb: 4, color: "white", fontWeight: "bold", textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
            שלום {user.displayName?.split(" ")[0] || user.email}!
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 3,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {user ? (
            <Button
              variant="contained"
              size="large"
              startIcon={<CalendarMonth />}
              component={Link}
              to="/booking"
              onClick={() => trackEvent('home_booking_button_clicked', { user_email: user.email })}
              sx={{ 
                minWidth: 200, 
                py: 2,
                '& .MuiButton-startIcon': {
                  marginLeft: '8px',
                  marginRight: 0
                }
              }}
            >
              להזמנת חדרים
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={<Login />}
              component={Link}
              to="/signin"
              onClick={() => trackEvent('home_signin_button_clicked')}
              sx={{ minWidth: 200, py: 2 }}
            >
              התחבר למערכת
            </Button>
          )}
          <Button
            variant="outlined"
            size="large"
            startIcon={<Info />}
            component={Link}
            to="/about"
            onClick={() => trackEvent('home_about_button_clicked', { user_email: user?.email })}
            sx={{ 
              minWidth: 200, 
              py: 2,
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)'
              },
              '& .MuiButton-startIcon': {
                marginLeft: '8px',
                marginRight: 0
              }
            }}
          >
            מידע נוסף
          </Button>
        </Box>
      </Box>
    </Container>
    </Box>
  );
}

function About() {
  return (
    <BackgroundWrapper>
      <Container maxWidth="lg" sx={{ py: 8, direction: "rtl" }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{ mb: 4, textAlign: "center", color: "white", fontWeight: "bold", textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}
        >
          מי אנחנו?
        </Typography>
        <Typography
          variant="h2"
          component="h1"
          sx={{ mb: 4, textAlign: "center", color: "white", fontWeight: "bold", textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}
        >
          האב חומת שמואל
        </Typography>
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h5" sx={{ mb: 4, color: "white", fontWeight: "bold", textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
            מערכת הזמנת חדרים ועמדות עבור Hub חומת שמואל{" "}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, mx: "auto", color: "white", textShadow: "1px 1px 2px rgba(0,0,0,0.7)" }}>
            המערכת מאפשרת הזמנה קלה ונוחה של חדרים ותחנות שונות במתחם חומת שמואל
            האב. ניתן לבחור מבין סוגי חדרים שונים, לבחור תאריכים וזמנים מתאימים,
            ולבצע הזמנה בצורה מהירה ופשוטה.
          </Typography>
        </Box>

      <Divider sx={{ my: 6 }} />

      {/* New Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
          bgcolor: "white",
          p: 4,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Typography variant="h4" component="h2" sx={{ mb: 2, color: "primary.main" }}>
          אם כבר עובדים, אז לפחות בכיף
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 4,
            flexWrap: "wrap",
            mt: 3,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Coffee sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              מטבחון
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Print sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              ציוד משרדי
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Groups sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              חדר ישיבות
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <LocalBar sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Happy hours
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <People sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Meetups
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: 3,
          bgcolor: "#b96948",
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Typography variant="h4" component="h2" sx={{ mb: 1, color: "white" }}>
          צור קשר
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, color: "white" }}>
          אנחנו כאן בשבילכם! צרו קשר איתנו בכל שאלה או בקשה
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <Card sx={{ height: "100%", textAlign: "center" }}>
            <CardContent sx={{ py: 2 }}>
              <Phone sx={{ fontSize: 32, color: "black", mb: 1 }} />
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                טלפון
              </Typography>
              <Typography variant="body2" color="text.secondary">
                02-6453891
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ height: "100%", textAlign: "center" }}>
            <CardContent sx={{ py: 2 }}>
              <Email sx={{ fontSize: 32, color: "black", mb: 1 }} />
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                אימייל
              </Typography>
              <Typography variant="body2" color="text.secondary">
                hub.homat.s@gmail.com
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                נענה תוך 24 שעות
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ height: "100%", textAlign: "center" }}>
            <CardContent sx={{ py: 2 }}>
              <LocationOn sx={{ fontSize: 32, color: "black", mb: 1 }} />
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                כתובת
              </Typography>
              <Typography variant="body2" color="text.secondary">
               שאול אביגור 7, הר חומה
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                ירושלים, ישראל
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ height: "100%", textAlign: "center" }}>
            <CardContent sx={{ py: 2 }}>
              <AccessTime sx={{ fontSize: 32, color: "black", mb: 1 }} />
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                שעות פעילות
              </Typography>
              <Typography variant="body2" color="text.secondary">
                א'-ה' 08:30-20:30
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box
        sx={{
          textAlign: "center",
          bgcolor: "primary.50",
          p: 4,
          borderRadius: 2,
          mt: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: "white" }}>
          שאלות נוספות?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: "white" }}>
          אם יש לכם שאלות נוספות או שאתם זקוקים לעזרה עם המערכת, אנא אל תהססו
          לפנות אלינו. אנחנו כאן כדי לעזור!
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<Email />}
          href="mailto:hub.homat.s@gmail.com"
          onClick={() => trackEvent('contact_email_clicked', { source: 'about_page' })}
          sx={{ 
            minWidth: 200,
            '& .MuiButton-startIcon': {
              marginLeft: '8px',
              marginRight: 0
            }
          }}
        >
          שלחו לנו הודעה
        </Button>
        </Box>
      </Container>
    </BackgroundWrapper>
  );
}

// Protected Route component
interface ProtectedRouteProps {
  user: User | null;
  children: React.ReactNode;
}

function ProtectedRoute({ user, children }: ProtectedRouteProps) {
  if (!user) {
    return <Navigate to="/signin" replace />;
  }
  return <>{children}</>;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Initialize PostHog on app start
  useEffect(() => {
    initializePostHog();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);

      // Track user authentication state
      if (user) {
        // Identify user in PostHog
        identifyUser(user.uid, {
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified,
        });
        trackEvent('user_signed_in', {
          email: user.email,
          displayName: user.displayName,
        });
      } else {
        // Reset PostHog user when signing out
        resetUser();
      }
    });

    return () => unsubscribe();
  }, []);

  // Check admin status when user changes
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const adminStatus = await isUserAdmin(user.email);
        setIsAdmin(adminStatus);
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  const handleSignOut = async () => {
    try {
      // Track logout event
      trackEvent('user_signed_out', {
        email: user?.email,
        displayName: user?.displayName,
      });
      
      // Log logout before signing out
      await auditLog.logout();
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <Typography variant="h6">טוען...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box
          sx={{
            flexGrow: 1,
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AppBar
            position="static"
            sx={{ direction: "rtl", backgroundColor: "#24282B" }}
          >
            <Toolbar>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexGrow: 1,
                }}
              >
                <Typography variant="h6" component="div">
                  Hub חומת שמואל
                </Typography>
              </Box>
              {user ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1, color: "white" }}>
                    שלום, {user.displayName?.split(" ")[0] || user.email}
                  </Typography>
                  {isAdmin && (
                    <IconButton
                      component={Link}
                      to="/admin"
                      color="inherit"
                      title="פאנל מנהל"
                      onClick={() => trackEvent('admin_panel_accessed', { user_email: user.email })}
                      sx={{ color: "white" }}
                    >
                      <AdminPanelSettings />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={handleSignOut}
                    color="inherit"
                    title="התנתק"
                    sx={{ color: "white" }}
                  >
                    <Logout />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  color="inherit"
                  startIcon={<Login />}
                  component={Link}
                  to="/signin"
                  onClick={() => trackEvent('header_signin_button_clicked')}
                  sx={{ color: "white" }}
                >
                  התחברות
                </Button>
              )}
            </Toolbar>
          </AppBar>

          <Navigation user={user} />

          <Box sx={{ flexGrow: 1 }}>
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<Home user={user} />} />
                <Route path="/about" element={<About />} />
                <Route
                  path="/signin"
                  element={user ? <Navigate to="/" replace /> : <SignIn />}
                />
                <Route
                  path="/booking"
                  element={
                    <ProtectedRoute user={user}>
                      <ErrorBoundary>
                        <Booking />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-bookings"
                  element={
                    <ProtectedRoute user={user}>
                      <ErrorBoundary>
                        <MyBookings user={user} />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute user={user}>
                      <ErrorBoundary>
                        <Admin user={user} />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </ErrorBoundary>
          </Box>

          <Box
            component="footer"
            sx={{
              py: 2,
              px: 2,
              textAlign: "center",
              backgroundColor: "#24282B",
              color: "white",
              direction: "rtl",
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              Powered by{" "}
              <Box
                component="a"
                href="mailto:ytzlax@gmail.com"
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Itzhak Lax
              </Box>
            </Typography>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
